import { useState, useEffect, useContext, useMemo, useCallback } from "react";
import { useLocation, useParams } from "react-router";
import { useNavigate } from "react-router-dom";

import {
    getOneTimeUrl,
    requestAzureSSOAuthentication,
    requestXecurifySSOAuthentication,
    setSessionToken,
} from "../../utils/api";

import { DashboardContext } from "../../context/DashboardContext";
import AuthSSOProcess from "./AuthSSOProcess";
import { CLIENT_MAPPING } from "../../utils/utils";
import { AUTH_PROVIDERS, AUTH_TYPE } from "../../utils/constants";

const PENDING_SSO_TARGET_STORAGE_KEY = "pendingSSOTarget";

function useQuery() {
    const { search } = useLocation();
    return useMemo(() => new URLSearchParams(search), [search]);
}

function AuthSSO() {
    const { authProvider, authType, authClient } = useParams();
    const [authenticated, setAuthenticated] = useState(false);
    const [authenticationFailed, setAuthenticationFailed] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");

    const context = useContext(DashboardContext);
    const navigate = useNavigate();

    let query = useQuery();
    const queryIdToken = query.get("id_token");
    const queryCode = query.get("code");
    const queryState = query.get("state");
    const querySessionState = query.get("session_state");

    const pendingSSOTarget = useMemo(() => {
        try {
            const storedTarget = sessionStorage.getItem(PENDING_SSO_TARGET_STORAGE_KEY);
            return storedTarget ? JSON.parse(storedTarget) : null;
        } catch (error) {
            console.error("Failed to parse pending SSO target:", error);
            return null;
        }
    }, []);

    const resolvedAuthType = authType || pendingSSOTarget?.authType || null;
    const resolvedAuthClient = authClient || pendingSSOTarget?.authClient || null;

    const handleGetOneTimeUrl = async (type, client) => {
        if (type === AUTH_TYPE.APP) {
            return await getOneTimeUrl(client);
        } else {
            console.error("Invalid authentication type");
            throw new Error("Invalid authentication type");
        }
    };

    const handleRedirectionToApp = useCallback(async () => {
        try {
            // Check if authClient is in CLIENT MAPPING, then use its mapped value or default to authClient
            if (!resolvedAuthType || !resolvedAuthClient) {
                throw new Error("Missing authentication target");
            }
            
            const client = CLIENT_MAPPING[resolvedAuthClient.toLowerCase()] || resolvedAuthClient;
            const oneTimeUrl = await handleGetOneTimeUrl(resolvedAuthType, client);

            sessionStorage.removeItem(PENDING_SSO_TARGET_STORAGE_KEY);

            setTimeout(() => {
                try {
                    // Redirection logic
                    window.location.href = oneTimeUrl; // Redirect to the app URL (no popup)
                } catch (err) {
                    console.error("Error while redirecting:", err);
                    throw new Error(`Error during redirection: ${err.message}`);
                }
            }, 1000);
        } catch (err) {
            console.error("Error while getting one-time URL:", err);
            throw new Error(`Error while getting one-time URL: ${err.message}`);
        }
    }, [resolvedAuthClient, resolvedAuthType]);

    useEffect(() => {
        const handleSSOAuthentication = async () => {
            if (!context.brandingLoading) {
                try {
                    if (authProvider) {
                        if (authProvider === AUTH_PROVIDERS.XECURIFY) {
                            if (!queryIdToken) {
                                throw new Error("Missing id_token parameter");
                            }

                            // Verify the JWT token
                            const xecurifySSOResponse =
                                await requestXecurifySSOAuthentication(queryIdToken);

                            setAuthenticated(true);
                            setSessionToken(xecurifySSOResponse.data); // Store verified token/session

                            if (resolvedAuthClient && resolvedAuthType) {
                                await handleRedirectionToApp();
                            } else {
                                setTimeout(() => {
                                    context.setLoggedIn(true);
                                    navigate("/");
                                }, 2000);
                            }
                        } else {
                            throw new Error("Invalid authentication provider");
                        }
                    } else {
                        // Default to Azure SSO Authentication if there is no Auth Provider
                        if (queryCode && queryState && querySessionState) {
                            // Handle Azure SSO Authentication
                            const azureSSOAuthenticationResponse =
                                await requestAzureSSOAuthentication(
                                    queryCode,
                                    queryState,
                                    querySessionState
                                );

                            setAuthenticated(true);
                            setSessionToken(azureSSOAuthenticationResponse.data);

                            if (resolvedAuthClient && resolvedAuthType) {
                                await handleRedirectionToApp();
                            } else {
                                setTimeout(() => {
                                    context.setLoggedIn(true);
                                    navigate("/");
                                }, 2000);
                            }
                        }
                    }
                } catch (error) {
                    setAuthenticationFailed(true);
                    setAuthenticated(false);
                    setErrorMessage(error.message);
                }
            }
        };
        handleSSOAuthentication();
    }, [authProvider, authType, authClient, context, navigate, handleRedirectionToApp, queryIdToken, queryCode, queryState, querySessionState, resolvedAuthClient, resolvedAuthType]);

    return (
        <AuthSSOProcess
            authenticated={authenticated}
            authenticationFailed={authenticationFailed}
            errorMessage={errorMessage}
        />
    );
}

export default AuthSSO;
